import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect, useState } from "react"

import EquivalentProductGroupsFilterChips from "../components/EquivalentProductGroupsFilterChips"
import { EquivalentProductGroupsFilterDrawer } from "../components/EquivalentProductGroupsFilterDrawer"
import EquivalentProductGroupFormDialog from "../dialogs/EquivalentProductGroupFormDialog"
import { useEquivalentProductGroupActions } from "../hooks/useEquivalentProductGroupActions"
import useEquivalentProductGroupSearchList from "../hooks/useEquivalentProductGroupSearchList"
import EquivalentProductGroupsTable from "../tables/EquivalentProductGroupsTable"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { EquivalencyGroup, EquivalencyGroupSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const EquivalentProductsContainer: FC = () => {
  const navigate = useNavigate({ from: "/app/admin/manage-products/equivalents" })

  const [isFormDialogOpen, setIsFormDialogOpen] = useState<boolean>(false)
  const [editingGroup, setEditingGroup] = useState<EquivalencyGroup | undefined>(undefined)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)

  const { data, fetching, error, searchParams, refetchData, clearStore } = useEquivalentProductGroupSearchList()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  // Load data with filters from URL
  useEffect(() => {
    refetchData(false)
  }, [refetchData])

  const { deleteEquivalentProductGroup } = useEquivalentProductGroupActions({
    onSuccess: () => refetchData(),
  })

  const toggleFilterDrawer = () => setShowFilterDrawer((prev) => !prev)

  const handleOpenFormDialog = (group?: EquivalencyGroup) => {
    setEditingGroup(group)
    setIsFormDialogOpen(true)
  }

  const handleCloseFormDialog = () => {
    setEditingGroup(undefined)
    setIsFormDialogOpen(false)
  }

  const handleSuccess = () => {
    refetchData()
  }

  const handleEdit = (group: EquivalencyGroup) => {
    handleOpenFormDialog(group)
  }

  const handleDelete = async (equivalencyGroupId: string): Promise<boolean> => {
    const success = await deleteEquivalentProductGroup({ equivalencyGroupId })
    return success
  }

  const sorting = searchParams.field
    ? [
        {
          id: upperSnakeCaseToCamelCase(searchParams.field),
          desc: searchParams.order === SortingOrder.Desc,
        },
      ]
    : []

  const handleSortingChange = (updaterFn: (state: any) => any) => {
    const newSorting = updaterFn(sorting)
    if (!newSorting.length) {
      return
    }

    const field = toUpperSnakeCase(newSorting[0].id) as EquivalencyGroupSortField
    const order: SortingOrder = newSorting[0].desc ? SortingOrder.Desc : SortingOrder.Asc
    const params = {
      query: searchParams.query,
      field,
      order,
      equivalentTypes: searchParams.equivalentTypes,
      productStatuses: searchParams.productStatuses,
      associationTypes: searchParams.associationTypes,
    }

    navigate({
      search: params,
      to: "/app/admin/manage-products/equivalents",
      replace: true,
    })
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <TableHeaderActions
        currentSearch={searchParams}
        placeholder='Search by Group Name, CAS#, Product Name or Chemical Name'
      >
        <Button
          startIcon={<FilterAltOutlined />}
          appearance='outlined'
          onClick={toggleFilterDrawer}
          data-testid='filter-button'
        >
          Filter
        </Button>
        <Button
          appearance='outlined'
          startIcon={<AddOutlined />}
          onClick={() => handleOpenFormDialog()}
          data-testid='new-equivalent-group-button'
        >
          New Group
        </Button>
      </TableHeaderActions>

      <EquivalentProductGroupsFilterChips />

      <Box className='grow overflow-hidden'>
        <EquivalentProductGroupsTable
          data={data}
          fetching={fetching}
          error={error}
          sorting={sorting}
          onHandleSortingChange={handleSortingChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>

      {isFormDialogOpen && (
        <EquivalentProductGroupFormDialog
          data={editingGroup}
          onSuccess={handleSuccess}
          onClose={handleCloseFormDialog}
        />
      )}

      <EquivalentProductGroupsFilterDrawer isOpen={showFilterDrawer} onToggle={toggleFilterDrawer} />
    </Box>
  )
}

export default EquivalentProductsContainer
